import {
  Component,
  Input,
  Output,
  EventEmitter,
  ElementRef,
  ViewChild,
  ViewChildren,
  QueryList,
  OnChanges,
  SimpleChanges,
  AfterViewInit,
  OnDestroy,
  Renderer2,
  HostListener,
} from "@angular/core";
import { CdkDragDrop } from "@angular/cdk/drag-drop";
import { CdkVirtualScrollViewport } from "@angular/cdk/scrolling";
import { ClippingStudioState } from "app/js/src/domain/clipping-studio/clipping-studio-state";
import { ClippingStudioClip } from "app/js/src/domain/clipping-studio/clipping-studio-clip";
import { ClippingStudioMarker } from "app/js/src/domain/clipping-studio/clipping-studio-marker";
import {
  ClippingStudioTextEvent,
  ClippingStudioGraphicEvent,
  ClippingStudioSubtitleEvent,
} from "app/js/src/domain/clipping-studio/clipping-studio-project";
import { ClippingStudioMamItem } from "app/js/src/domain/clipping-studio/clipping-studio-mam-item";
import {
  TimelineThumbnailVttService,
  ThumbnailCue,
} from "app/js/src/infrastructure/clipping-studio/timeline-thumbnail-vtt.service";
import { ClippingStudioManagerService } from "app/js/src/infrastructure/clipping-studio/clipping-studio-manager.service";
import { isVisibleOnSourceTimeline } from "app/js/src/infrastructure/clipping-studio/clipping-studio-mam-file-time";
import {
  MAIN_AUDIO_EMBEDDED_LABEL,
  mainAudioTrackFullLabel,
  resolveMainAudioSelection,
  truncateMainAudioLabel,
} from "app/js/src/infrastructure/clipping-studio/clipping-studio-main-audio";
import { ClippingStudioMainAudioTrack } from "app/js/src/domain/clipping-studio/clipping-studio-main-audio-track";

interface TimeBlock {
  timestamp: string;
  width: number;
  ticks: { x: number; y1: number; y2: number; width: number }[];
}

/** Set to true to log seek/playhead flow (e.g. after reorder). */
const DEBUG_SEEK = false;
function csSeekLog(...args: unknown[]): void {
  if (DEBUG_SEEK) console.log("[CS seek]", ...args);
}

/** When zoomed: fixed pixel width per block so tick spacing stays constant; only block duration (time per division) and scroll change. */
@Component({
  selector: "evrideo-clipping-studio-timeline",
  templateUrl: "./clipping-studio-timeline.component.html",
  styleUrls: ["./clipping-studio-timeline.component.scss"],
})
export class ClippingStudioTimelineComponent
  implements OnChanges, AfterViewInit, OnDestroy
{
  /** When set, state is read from manager (source of truth) so playhead/seek stay correct after clip reorder. */
  @Input() manager: ClippingStudioManagerService | null = null;

  private _stateInput: ClippingStudioState | undefined;
  @Input() set state(s: ClippingStudioState | undefined) {
    this._stateInput = s;
  }
  get state(): ClippingStudioState | undefined {
    return this.manager?.state ?? this._stateInput;
  }
  /** Effective graphics for the selected preview destination (base + destination overrides/only). When set, Graphics track uses this instead of state.graphics. */
  @Input() effectiveGraphics: ClippingStudioGraphicEvent[] | null = null;
  /** Effective text for the selected preview destination (base + destination overrides/only). When set, Text track uses this instead of state.text. */
  @Input() effectiveText: ClippingStudioTextEvent[] | null = null;
  @Input() audioMuted = false;
  @Input() delayMs: number | null = null;
  /** When true, timeline is non-interactive (e.g. while MAM media is loading). Blocks drop and pointer. */
  @Input() timelineDisabled = false;
  /** Block duration in seconds from parent zoom. null = fit-to-container (auto). */
  @Input() blockDuration: number | null = null;
  /** When set (SOURCE + MAM), timeline shows only this segment; ruler and seek are segment-relative. */
  @Input() zoomedSegmentIndex: number | null = null;
  @Output() seekTo = new EventEmitter<number>();
  @Output() clipSelected = new EventEmitter<string | null>();
  @Output() clipReordered = new EventEmitter<string[]>();
  @Output() markerSelected = new EventEmitter<string | null>();
  @Output() markerTextChange = new EventEmitter<{
    markerId: string;
    text: string;
  }>();
  @Output() audioMutedChange = new EventEmitter<boolean>();
  @Output() mainAudioSelectionChange = new EventEmitter<number | null>();
  @Output() textSelected = new EventEmitter<string>();
  @Output() graphicSelected = new EventEmitter<string>();
  @Output() subtitleSelected = new EventEmitter<string>();
  @Output() mamAddRequest = new EventEmitter<ClippingStudioMamItem>();

  /** Empty drop-list data for the MAM → timeline zone (payload is on the drag item). */
  readonly mamDropListData: unknown[] = [];

  @ViewChild("timeline") Timeline: ElementRef<HTMLDivElement>;
  @ViewChild("timelineVirtualScroll")
  TimelineVirtualScroll?: CdkVirtualScrollViewport;
  @ViewChild("timelineScrollZone")
  TimelineScrollZone?: ElementRef<HTMLElement>;
  @ViewChild("playhead") Playhead: ElementRef<HTMLDivElement>;
  @ViewChild("segments") SegmentsContainer: ElementRef<HTMLDivElement>;
  @ViewChild("tracks") TracksContainer: ElementRef<HTMLDivElement>;
  @ViewChildren("track") Tracks: QueryList<ElementRef>;

  timeBlocks: TimeBlock[] = [];
  private _timelineBlocksLength = 0;
  timelineBlockDuration = 10;
  timelineBlockWidth = 0;
  componentHeight = 0;
  playheadLineHeight = 0;

  // Timeline drag (copied from content-control: enables panning when zoomed)
  isTimelineDragging = false;
  timelineStartPos = 0;
  timelineMouseDownPos = -1;

  // Playhead drag
  isPlayheadDragging = false;
  playheadCurrentLeft = 0;
  playheadPositionInMs = 0;
  playheadHalfWidth = 6.5;
  playheadStartPos = 0;
  playheadPreviousLeft = 0;

  // Tooltip
  showTooltip = false;
  tooltipPosition = { x: 0, y: 0 };
  tooltipCurrentTime = 0;
  tooltipFormattedCurrentTime = "00:00:00.000";

  /** Parsed VTT cues for thumbnail sprite (loaded when media has thumbnailsSpriteWebVTTUrl). */
  thumbnailCues: ThumbnailCue[] | null = null;

  trackLabels = ["Video", "Markers", "Subtitles", "Text", "Graphics", "Audio"];

  /**
   * Label-row fade in source mode. Audio is omitted so the track picker/mute stay opaque;
   * the Audio text label is faded separately via trackLabelFaded.
   */
  get disabledTracks(): Set<string> {
    return this.isSourceMode
      ? new Set(["Text", "Graphics"])
      : new Set<string>();
  }

  /** True when the track lane should be greyed out (source mode). Audio lane stays disabled. */
  isTrackDisabled(label: string): boolean {
    return this.isSourceMode && ["Text", "Graphics", "Audio"].includes(label);
  }

  /** Fade only the Audio text label in source mode (not the picker controls). */
  isTrackLabelFaded(label: string): boolean {
    return this.isSourceMode && label === "Audio";
  }

  utcTimeString = "00:00:00.000";

  private resizeObserver: ResizeObserver;
  private boundDoDrags: (e: MouseEvent) => void;
  private boundClearDrags: (e: MouseEvent) => void;
  private boundScrollClamp: () => void;
  private utcIntervalId: ReturnType<typeof setInterval> | null = null;
  private animationRafId: number | null = null;

  constructor(
    private renderer: Renderer2,
    private thumbnailVtt: TimelineThumbnailVttService,
  ) {}

  ngAfterViewInit(): void {
    this.boundDoDrags = (e) => this.doDrags(e);
    this.boundClearDrags = (e) => this.clearDrags(e);
    this.boundScrollClamp = () => this.clampScrollToMediaEnd();
    window.addEventListener("mousemove", this.boundDoDrags);
    window.addEventListener("mouseup", this.boundClearDrags);

    this.getScrollContainer()?.addEventListener("scroll", this.boundScrollClamp);

    this.resizeObserver = new ResizeObserver(() => this.recalculate());
    if (this.Timeline?.nativeElement) {
      this.resizeObserver.observe(this.Timeline.nativeElement);
    }
    this.recalculate();

    this.utcIntervalId = setInterval(() => this.updateUtcTime(), 1000);
    this.updateUtcTime();

    this.animationRafId = requestAnimationFrame((t) => this.animationLoop(t));
  }

  ngOnDestroy(): void {
    if (this.animationRafId != null) {
      cancelAnimationFrame(this.animationRafId);
      this.animationRafId = null;
    }
    this.getScrollContainer()?.removeEventListener(
      "scroll",
      this.boundScrollClamp,
    );
    this.resizeObserver?.disconnect();
    window.removeEventListener("mousemove", this.boundDoDrags);
    window.removeEventListener("mouseup", this.boundClearDrags);
    if (this.utcIntervalId != null) {
      clearInterval(this.utcIntervalId);
      this.utcIntervalId = null;
    }
  }

  /** Clamp scroll so viewport cannot go past the last frame (e.g. scrollbar/wheel). */
  private clampScrollToMediaEnd(): void {
    if (this.isFitted) return;
    const vs = this.getScrollContainer();
    if (!vs) return;
    const maxScroll = this.getMaxScrollLeft();
    if (vs.scrollLeft > maxScroll) {
      vs.scrollLeft = maxScroll;
      this.translateTracks(maxScroll);
    }
  }

  /**
   * Same as content-control: run every frame to keep playhead and time in sync.
   */
  private animationLoop(currentTime: number): void {
    this.updatePlayheadPosition();
    this.animationRafId = requestAnimationFrame((t) => this.animationLoop(t));
  }

  /** Effective duration (ms) for a state snapshot; used to detect when timeline length must be rebuilt. */
  private static getEffectiveDurationMs(s: ClippingStudioState | null): number {
    if (!s) return 0;
    if (s.mode === "EDIT" && s.clips?.length) {
      const ordered = [...s.clips].sort((a, b) => a.order - b.order);
      const sum = ordered.reduce((acc, c) => acc + (c.outMs - c.inMs), 0);
      if (sum > 0) return sum;
    }
    const main = s.media?.durationMs ?? 0;
    const appended = s.appendedMamSegments ?? [];
    const appendedTotal = appended.reduce((acc, seg) => acc + seg.durationMs, 0);
    return main + appendedTotal;
  }

  /** Ordered clip ids string for change detection; recalc when order changes so seek/playhead work after reorder. */
  private static getOrderedClipIds(s: ClippingStudioState | null): string {
    if (!s?.clips?.length) return "";
    const ordered = [...s.clips].sort((a, b) => a.order - b.order);
    return ordered.map((c) => c.id).join(",");
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes["blockDuration"]) {
      if (this.blockDuration == null) {
        this.recalculate();
      } else {
        this.recalculate();
      }
    }
    if (changes["zoomedSegmentIndex"]) {
      this.recalculate();
    }
    if (changes["state"]) {
      this.updatePlayheadPosition();
      const prev = changes["state"].previousValue as
        | ClippingStudioState
        | undefined;
      const curr = changes["state"].currentValue as
        | ClippingStudioState
        | undefined;
      const prevEffective =
        ClippingStudioTimelineComponent.getEffectiveDurationMs(prev ?? null);
      const currEffective =
        ClippingStudioTimelineComponent.getEffectiveDurationMs(curr ?? null);
      const mediaChanged =
        (!prev?.media && curr?.media) ||
        prev?.media?.durationMs !== curr?.media?.durationMs;
      const effectiveDurationChanged = prevEffective !== currEffective;
      const clipOrderChanged =
        (curr?.mode === "EDIT" && (curr?.clips?.length ?? 0) > 0 &&
          ClippingStudioTimelineComponent.getOrderedClipIds(prev) !==
            ClippingStudioTimelineComponent.getOrderedClipIds(curr));
      if (mediaChanged || effectiveDurationChanged || clipOrderChanged) {
        this.recalculate();
      }
      this.loadThumbnailCuesIfNeeded();
    }
  }

  private loadThumbnailCuesIfNeeded(): void {
    const vttUrl = this.state?.media?.thumbnailsSpriteWebVTTUrl;
    if (!vttUrl) {
      this.thumbnailCues = null;
      return;
    }
    this.thumbnailVtt.getCues(vttUrl).then((cues) => {
      this.thumbnailCues = cues;
    });
  }

  /**
   * Style object for the tooltip thumbnail (sprite region). Null when no media/sprite or no cue for current time.
   * Uses "last available" rule: cue with largest startMs <= timeMs (or first cue if before first).
   * In EDIT mode, tooltip time is edit time so we convert to source time for the sprite lookup.
   */
  get tooltipThumbnailStyle(): { [key: string]: string } | null {
    const spriteUrl = this.state?.media?.thumbnailsSpriteUrl;
    const cues = this.thumbnailCues;
    if (!spriteUrl || !cues?.length) return null;

    const timelineTimeMs = this.tooltipCurrentTime * 1000;
    let timeMs: number;
    if (this.state?.mode === "EDIT" && this.clips.length > 0) {
      timeMs = this.editToSourceTimeMs(timelineTimeMs);
    } else if (
      this.zoomedSegmentIndex != null &&
      this.useSegmentRulerBase
    ) {
      timeMs = this.zoomedSegmentStartMs + timelineTimeMs;
    } else {
      timeMs = timelineTimeMs;
    }
    const mainDurationMs = this.state?.media?.durationMs ?? 0;
    if (timeMs >= mainDurationMs) return null;

    const eligible = cues.filter((c) => c.startMs <= timeMs);
    const cue = eligible.length ? eligible[eligible.length - 1] : cues[0];

    return {
      backgroundImage: `url(${spriteUrl})`,
      backgroundPosition: `-${cue.x}px -${cue.y}px`,
      backgroundRepeat: "no-repeat",
    };
  }

  // --------------- Fitted vs Zoomed (from content-control) ---------------

  /** When blockDuration is null the timeline fits-to-container (no scrolling). */
  get isFitted(): boolean {
    return this.blockDuration == null;
  }

  // --------------- Data getters ---------------

  /**
   * In SOURCE mode: full media duration + appended MAM segments.
   * In EDIT mode: sum of cut clip lengths (total edited sequence length).
   * When zoomedSegmentIndex is set (SOURCE + MAM): only that segment's duration.
   */
  get durationMs(): number {
    if (this.zoomedSegmentIndex != null && this.useSegmentRulerBase) {
      const mainMs = this.state?.media?.durationMs ?? 0;
      const segments = this.state?.appendedMamSegments ?? [];
      if (this.zoomedSegmentIndex === 0) return mainMs;
      const idx = this.zoomedSegmentIndex - 1;
      if (idx >= 0 && idx < segments.length) return segments[idx].durationMs;
      return mainMs;
    }
    if (this.state?.mode === "EDIT" && this.clips.length > 0) {
      const sum = this.clipsByOrder.reduce((s, c) => s + (c.outMs - c.inMs), 0);
      if (sum > 0) return sum;
    }
    const main = this.state?.media?.durationMs ?? 0;
    const appended = this.state?.appendedMamSegments ?? [];
    const appendedTotal = appended.reduce((s, seg) => s + seg.durationMs, 0);
    return main + appendedTotal;
  }

  /** Start time (ms) of the zoomed segment in global timeline. 0 when not in zoomed-segment mode. */
  get zoomedSegmentStartMs(): number {
    if (this.zoomedSegmentIndex == null || !this.useSegmentRulerBase) return 0;
    if (this.zoomedSegmentIndex <= 0) return 0;
    const mainMs = this.state?.media?.durationMs ?? 0;
    const segments = this.state?.appendedMamSegments ?? [];
    let cursor = mainMs;
    for (let i = 0; i < this.zoomedSegmentIndex - 1 && i < segments.length; i++) {
      cursor += segments[i].durationMs;
    }
    return cursor;
  }

  get durationSec(): number {
    return this.durationMs / 1000;
  }

  /** Map source time (ms) to edit timeline position (0..durationMs). Used in EDIT mode. */
  sourceToEditTimeMs(sourceMs: number): number {
    let sum = 0;
    for (const c of this.clipsByOrder) {
      if (sourceMs >= c.inMs && sourceMs <= c.outMs)
        return sum + (sourceMs - c.inMs);
      sum += c.outMs - c.inMs;
    }
    return 0;
  }

  /** Map edit timeline position (0..durationMs) to source time (ms). Used in EDIT mode. */
  editToSourceTimeMs(editMs: number): number {
    let sum = 0;
    for (const c of this.clipsByOrder) {
      const clipLen = c.outMs - c.inMs;
      if (editMs <= sum + clipLen) return c.inMs + (editMs - sum);
      sum += clipLen;
    }
    const last = this.clipsByOrder[this.clipsByOrder.length - 1];
    return last ? last.outMs : 0;
  }

  get clips(): ClippingStudioClip[] {
    return this.state?.clips ?? [];
  }

  /** In Source mode: main cuts and cuts from appended MAM; hide Edit-only whole-file MAM clips. */
  get sourceModeClips(): ClippingStudioClip[] {
    const list = this.clips;
    if (this.state?.mode !== "SOURCE") return list;
    return list.filter((c) => isVisibleOnSourceTimeline(c));
  }

  /** True when the clip should appear / highlight on the Source video track. */
  isSourceTimelineClip(clip: ClippingStudioClip | null): boolean {
    return clip != null && isVisibleOnSourceTimeline(clip);
  }

  get clipsByOrder(): ClippingStudioClip[] {
    const list = this.clips.slice();
    list.sort((a, b) => a.order - b.order);
    return list;
  }

  /** Selected clip for the segments-strip highlight; only set when user has clicked a segment. */
  get selectedClip(): ClippingStudioClip | null {
    const id = this.state?.selectedClipId;
    if (!id) return null;
    return this.clips.find((c) => c.id === id) ?? null;
  }

  get markers(): ClippingStudioMarker[] {
    return this.state?.markers ?? [];
  }

  /** In Source mode: all markers. In Edit mode: only manual markers (no MAM source markers). */
  get displayMarkers(): ClippingStudioMarker[] {
    const list = this.state?.markers ?? [];
    if (this.state?.mode !== "EDIT") return list;
    return list.filter((m) => !m.appendedSegmentId);
  }

  get textSegments(): ClippingStudioTextEvent[] {
    if (this.effectiveText != null) return this.effectiveText;
    return this.state?.text ?? [];
  }

  get graphicSegments(): ClippingStudioGraphicEvent[] {
    if (this.effectiveGraphics != null) return this.effectiveGraphics;
    return this.state?.graphics ?? [];
  }

  /** Returns the clip that contains the given edit timeline position (ms). */
  getClipAtEditTime(editMs: number): ClippingStudioClip | null {
    let sum = 0;
    for (const c of this.clipsByOrder) {
      const len = c.outMs - c.inMs;
      if (editMs >= sum && editMs < sum + len) return c;
      sum += len;
    }
    return null;
  }

  get subtitleSegments(): ClippingStudioSubtitleEvent[] {
    const list = this.state?.subtitles ?? [];
    if (this.state?.mode !== "EDIT" || this.clips.length === 0) return list;
    return list.filter((sub) => {
      if (sub.fromMainSource === false) return true;
      const leftEdit = this.sourceToEditTimeMs(sub.StartTimeMS);
      const clip = this.getClipAtEditTime(leftEdit);
      return clip != null && clip.mamMediaId == null;
    });
  }

  get isSourceMode(): boolean {
    return this.state?.mode === "SOURCE";
  }

  /** Left positions (0-100%) for vertical source-boundary markers on the ruler. One per appended MAM segment. */
  get sourceBoundaryPositionsPercent(): number[] {
    if (!this.isSourceMode) return [];
    if (this.zoomedSegmentIndex != null && this.useSegmentRulerBase) return [];
    const appended = this.state?.appendedMamSegments ?? [];
    if (appended.length === 0) return [];
    const totalMs = this.durationMs;
    if (totalMs <= 0) return [];
    const mainMs = this.state?.media?.durationMs ?? 0;
    const positions: number[] = [];
    let boundaryMs = mainMs;
    for (let i = 0; i < appended.length; i++) {
      positions.push((boundaryMs / totalMs) * 100);
      boundaryMs += appended[i].durationMs;
    }
    return positions;
  }

  /** Ranges for decorative fills over appended MAM segments on the ruler (odd/even opacity). When zoomed in, one fill for the current segment with same odd/even as in fit view. */
  get mamSegmentFillRanges(): {
    leftPercent: number;
    widthPercent: number;
    isOdd: boolean;
  }[] {
    if (!this.isSourceMode) return [];
    if (this.zoomedSegmentIndex != null && this.useSegmentRulerBase) {
      if (this.zoomedSegmentIndex === 0) return [];
      return [
        {
          leftPercent: 0,
          widthPercent: 100,
          isOdd: (this.zoomedSegmentIndex - 1) % 2 === 0,
        },
      ];
    }
    const appended = this.state?.appendedMamSegments ?? [];
    if (appended.length === 0) return [];
    const totalMs = this.durationMs;
    if (totalMs <= 0) return [];
    const mainMs = this.state?.media?.durationMs ?? 0;
    const result: { leftPercent: number; widthPercent: number; isOdd: boolean }[] = [];
    let cursorMs = mainMs;
    for (let i = 0; i < appended.length; i++) {
      const seg = appended[i];
      result.push({
        leftPercent: (cursorMs / totalMs) * 100,
        widthPercent: (seg.durationMs / totalMs) * 100,
        isOdd: i % 2 === 0,
      });
      cursorMs += seg.durationMs;
    }
    return result;
  }

  get pendingInPercent(): number | null {
    if (this.state?.pendingInMs == null || !this.durationMs) return null;
    const startMs =
      this.zoomedSegmentIndex != null && this.useSegmentRulerBase
        ? this.zoomedSegmentStartMs
        : 0;
    return ((this.state.pendingInMs - startMs) / this.durationMs) * 100;
  }

  get pendingOutPercent(): number | null {
    if (this.state?.pendingOutMs == null || !this.durationMs) return null;
    const startMs =
      this.zoomedSegmentIndex != null && this.useSegmentRulerBase
        ? this.zoomedSegmentStartMs
        : 0;
    return ((this.state.pendingOutMs - startMs) / this.durationMs) * 100;
  }

  get pendingRegion(): { left: number; width: number } | null {
    if (this.state?.pendingInMs == null || this.state?.pendingOutMs == null)
      return null;
    const startMs =
      this.zoomedSegmentIndex != null && this.useSegmentRulerBase
        ? this.zoomedSegmentStartMs
        : 0;
    const startPct =
      ((Math.min(this.state.pendingInMs, this.state.pendingOutMs) - startMs) /
        this.durationMs) *
      100;
    const endPct =
      ((Math.max(this.state.pendingInMs, this.state.pendingOutMs) - startMs) /
        this.durationMs) *
      100;
    return { left: startPct, width: endPct - startPct };
  }

  getClipPosition(clip: ClippingStudioClip): { left: number; width: number } {
    if (!this.durationMs) return { left: 0, width: 0 };
    const startMs =
      this.zoomedSegmentIndex != null && this.useSegmentRulerBase
        ? this.zoomedSegmentStartMs
        : 0;
    const left = ((clip.inMs - startMs) / this.durationMs) * 100;
    const width = ((clip.outMs - clip.inMs) / this.durationMs) * 100;
    return { left, width };
  }

  getEditClipFlex(clip: ClippingStudioClip): number {
    return clip.outMs - clip.inMs;
  }

  getMarkerPercent(marker: ClippingStudioMarker): number {
    if (!this.durationMs) return 0;
    const startMs =
      this.zoomedSegmentIndex != null && this.useSegmentRulerBase
        ? this.zoomedSegmentStartMs
        : 0;
    if (this.state?.mode === "EDIT") {
      const editMs = this.sourceToEditTimeMs(marker.inMs);
      return (editMs / this.durationMs) * 100;
    }
    return ((marker.inMs - startMs) / this.durationMs) * 100;
  }

  getTextSegmentPosition(te: ClippingStudioTextEvent): { left: number; width: number } {
    if (!this.durationMs) return { left: 0, width: 0 };
    const startMs =
      this.zoomedSegmentIndex != null && this.useSegmentRulerBase
        ? this.zoomedSegmentStartMs
        : 0;
    // Text inMs/outMs are stored in edit time; use them directly in EDIT mode.
    if (this.state?.mode === "EDIT" && this.clips.length > 0) {
      return {
        left: ((te.inMs - startMs) / this.durationMs) * 100,
        width: ((te.outMs - te.inMs) / this.durationMs) * 100,
      };
    }
    return {
      left: ((te.inMs - startMs) / this.durationMs) * 100,
      width: ((te.outMs - te.inMs) / this.durationMs) * 100,
    };
  }

  getGraphicSegmentPosition(g: ClippingStudioGraphicEvent): { left: number; width: number } {
    if (!this.durationMs) return { left: 0, width: 0 };
    const startMs =
      this.zoomedSegmentIndex != null && this.useSegmentRulerBase
        ? this.zoomedSegmentStartMs
        : 0;
    // Graphics inMs/outMs are stored in edit time; use them directly in EDIT mode.
    if (this.state?.mode === "EDIT" && this.clips.length > 0) {
      return {
        left: ((g.inMs - startMs) / this.durationMs) * 100,
        width: ((g.outMs - g.inMs) / this.durationMs) * 100,
      };
    }
    return {
      left: ((g.inMs - startMs) / this.durationMs) * 100,
      width: ((g.outMs - g.inMs) / this.durationMs) * 100,
    };
  }

  getSubtitleSegmentPosition(sub: ClippingStudioSubtitleEvent): { left: number; width: number } {
    if (!this.durationMs) return { left: 0, width: 0 };
    const startMs =
      this.zoomedSegmentIndex != null && this.useSegmentRulerBase
        ? this.zoomedSegmentStartMs
        : 0;
    if (this.state?.mode === "EDIT" && this.clips.length > 0) {
      if (sub.fromMainSource === false) {
        return {
          left: ((sub.StartTimeMS - startMs) / this.durationMs) * 100,
          width: ((sub.EndTimeMS - sub.StartTimeMS) / this.durationMs) * 100,
        };
      }
      const leftEdit = this.sourceToEditTimeMs(sub.StartTimeMS);
      const rightEdit = this.sourceToEditTimeMs(sub.EndTimeMS);
      const widthEdit = rightEdit - leftEdit;
      return {
        left: (leftEdit / this.durationMs) * 100,
        width: (widthEdit / this.durationMs) * 100,
      };
    }
    return {
      left: ((sub.StartTimeMS - startMs) / this.durationMs) * 100,
      width: ((sub.EndTimeMS - sub.StartTimeMS) / this.durationMs) * 100,
    };
  }

  /** Current time to show in the timeline (edit time in EDIT mode, source time otherwise). When zoomed into a segment in SOURCE mode, shows segment-relative time. */
  get displayTimeMs(): number | null {
    if (this.state?.currentTimeMs == null) return null;
    if (this.zoomedSegmentIndex != null && this.useSegmentRulerBase) {
      return this.state.currentTimeMs - this.zoomedSegmentStartMs;
    }
    return this.state.currentTimeMs;
  }

  // --------------- Timeline width / pixels (from content-control) ---------------

  /**
   * When fitted: container width (all blocks fit inside viewport).
   * When zoomed: total length of all time blocks in px (blocks exceed viewport).
   */
  get timelineWidth(): number {
    if (this.isFitted) {
      return this.Timeline?.nativeElement?.offsetWidth ?? 0;
    }
    return (
      this._timelineBlocksLength ||
      (this.Timeline?.nativeElement?.offsetWidth ?? 0)
    );
  }

  get getTimelineBlocksLength(): number {
    return this._timelineBlocksLength;
  }

  /**
   * Pixels per second of video.
   * When fitted: containerWidth / durationSec  (everything fits).
   * When zoomed: timelineBlocksLength / durationSec  (blocks are wider than container).
   */
  get pixelsPerSecond(): number {
    if (!this.durationSec) return 0;
    return this.timelineWidth / this.durationSec;
  }

  get virtualScrollLeft(): number {
    return this.getScrollLeft();
  }

  get useSegmentRuler(): boolean {
    return this.useSegmentRulerBase && this.zoomedSegmentIndex == null;
  }

  /** SOURCE mode with at least one MAM segment (used for zoomed-segment duration and ruler). */
  get useSegmentRulerBase(): boolean {
    return (
      this.state?.mode === "SOURCE" &&
      (this.state?.appendedMamSegments?.length ?? 0) > 0
    );
  }

  private getScrollContainer(): HTMLElement | null {
    return (
      this.TimelineVirtualScroll?.elementRef?.nativeElement ??
      this.TimelineScrollZone?.nativeElement ??
      null
    );
  }

  private getScrollLeft(): number {
    if (this.TimelineVirtualScroll) {
      return this.TimelineVirtualScroll.measureScrollOffset();
    }
    return this.TimelineScrollZone?.nativeElement?.scrollLeft ?? 0;
  }

  private setScrollLeft(leftPx: number): void {
    if (this.TimelineVirtualScroll) {
      this.TimelineVirtualScroll.scrollToOffset(leftPx);
      return;
    }
    if (this.TimelineScrollZone?.nativeElement) {
      this.TimelineScrollZone.nativeElement.scrollLeft = leftPx;
    }
  }

  pixelsToSecondsConvert(pixels: number): number {
    return pixels / this.pixelsPerSecond;
  }

  // --------------- Format ---------------

  formatTime(timeMs: number): string {
    const totalSeconds = Math.floor(timeMs / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    const ms = Math.floor(timeMs % 1000);
    const pad = (n: number, len = 2) => n.toString().padStart(len, "0");
    return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}.${pad(ms, 3)}`;
  }

  formatDelay(): string {
    if (this.delayMs == null) return "0.000 s";
    const sec = this.delayMs / 1000;
    return `${sec.toFixed(3)} s`;
  }

  private updateUtcTime(): void {
    const d = new Date();
    const pad = (n: number, len = 2) => n.toString().padStart(len, "0");
    this.utcTimeString = `${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}:${pad(d.getUTCSeconds())}.${pad(d.getUTCMilliseconds(), 3)}`;
  }

  // --------------- Timeline mouse events (from content-control) ---------------

  onTimelineMouseDown(event: MouseEvent): void {
    this.timelineMouseDownPos = this.timelineStartPos = event.clientX;
    if (!this.isFitted) {
      this.isTimelineDragging = true;
      event.preventDefault();
      event.stopImmediatePropagation();
    }
  }

  onMouseMove(event: MouseEvent): void {
    if (!this.durationMs) return;
    const sc = this.getScrollContainer();
    if (!sc) return;
    const rectLeft = sc.getBoundingClientRect().left;
    const mouseX = Math.max(
      0,
      event.clientX -
        rectLeft +
        this.getScrollLeft(),
    );
    if (mouseX === 0) {
      this.tooltipCurrentTime = 0;
      this.tooltipFormattedCurrentTime = "00:00:00.000";
    } else {
      const timeSec = mouseX / this.pixelsPerSecond;
      this.tooltipCurrentTime = timeSec;
      this.tooltipFormattedCurrentTime =
        this.zoomedSegmentIndex != null && this.useSegmentRulerBase
          ? this.formatTime(timeSec * 1000)
          : this.getRulerLabelForGlobalTimeSec(timeSec);
    }
    this.tooltipPosition = { x: event.clientX, y: event.clientY };
    this.showTooltip = true;
  }

  onMouseLeave(): void {
    this.showTooltip = false;
    this.tooltipCurrentTime = 0;
    this.tooltipFormattedCurrentTime = "00:00:00.000";
    this.tooltipPosition = { x: 0, y: 0 };
  }

  /**
   * Compute edit time (ms) from a mouse event over the timeline ruler/viewport.
   * Use this on click so the position is taken from the click, not from the last mousemove.
   */
  private getEditTimeMsFromRulerEvent(event: MouseEvent): number | null {
    if (!this.durationMs) return null;
    const sc = this.getScrollContainer();
    if (!sc) return null;
    const rectLeft = sc.getBoundingClientRect().left;
    const mouseX = Math.max(
      0,
      event.clientX -
        rectLeft +
        this.getScrollLeft(),
    );
    const timeSec = mouseX / this.pixelsPerSecond;
    const editMs = timeSec * 1000;
    if (DEBUG_SEEK) {
      csSeekLog("getEditTimeMsFromRulerEvent", {
        clientX: event.clientX,
        rectLeft,
        scrollLeft: this.getScrollLeft(),
        mouseX,
        pixelsPerSecond: this.pixelsPerSecond,
        durationMs: this.durationMs,
        editMs,
      });
    }
    return editMs;
  }

  changePlayheadPosition(event: MouseEvent): void {
    if (this.isPlayheadDragging || this.isTimelineDragging) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }
    const editMs = this.getEditTimeMsFromRulerEvent(event);
    if (editMs != null) {
      if (DEBUG_SEEK) csSeekLog("changePlayheadPosition (ruler click)", editMs, "editMs");
      this.goToTime(editMs);
    } else {
      event.preventDefault();
      event.stopPropagation();
    }
  }

  /**
   * Seek when clicking on the track area (not on a clip bar).
   * Tracks are a sibling of the timeline ruler, so ruler mousedown never fires for track clicks.
   */
  onTracksMouseDown(event: MouseEvent): void {
    const target = event.target as Node;
    if (target && (target as Element).closest?.(".edit-clip-bar, .clip-bar")) {
      return;
    }
    if (
      !this.durationMs ||
      !this.TracksContainer?.nativeElement ||
      !this.getScrollContainer()
    ) {
      return;
    }
    const rect = this.TracksContainer.nativeElement.getBoundingClientRect();
    const scrollLeft = this.getScrollLeft();
    const contentX = Math.max(
      0,
      event.clientX - rect.left + scrollLeft,
    );
    const timeSec = contentX / this.pixelsPerSecond;
    const timeMs = timeSec * 1000;
    if (DEBUG_SEEK) csSeekLog("onTracksMouseDown", timeMs, "editMs");
    this.goToTime(timeMs);
  }

  // --------------- Playhead events ---------------

  onPlayheadMouseDown(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    this.isPlayheadDragging = true;
    this.playheadStartPos = (event as MouseEvent).clientX;
  }

  // --------------- Drag logic (from content-control doDrags / clearDrags) ---------------

  doDrags(e: MouseEvent): void {
    const currentPosition = e.clientX;

    // Timeline pan (only when zoomed, not fitted)
    if (this.isTimelineDragging && !this.isFitted) {
      const vs = this.getScrollContainer();
      if (!vs) return;
      const dx = currentPosition - this.timelineStartPos;
      let nextScroll = vs.scrollLeft - dx;
      const maxScroll = this.getMaxScrollLeft();
      nextScroll = Math.max(0, Math.min(nextScroll, maxScroll));
      this.setScrollLeft(nextScroll);
      this.translateTracks(nextScroll);
      this.timelineStartPos = currentPosition;
    }

    // Playhead drag: only move the playhead visually; seek once on release to avoid many expensive seeks.
    if (this.isPlayheadDragging && this.Timeline?.nativeElement) {
      const dx = currentPosition - this.playheadStartPos;
      this.playheadCurrentLeft = !this.isFitted
        ? currentPosition -
          this.Timeline.nativeElement.getBoundingClientRect().x
        : this.playheadPreviousLeft + dx;

      if (
        this.playheadCurrentLeft <=
          this.timelineWidth - this.playheadHalfWidth &&
        this.playheadCurrentLeft >= 0
      ) {
        this.Playhead.nativeElement.style.left = `${this.playheadCurrentLeft - this.playheadHalfWidth}px`;
        const seconds = this.pixelsToSecondsConvert(
          this.playheadCurrentLeft + this.virtualScrollLeft,
        );
        this.playheadPositionInMs = seconds * 1000;
      }
    }
  }

  clearDrags(event?: MouseEvent): void {
    if (this.timelineMouseDownPos === this.timelineStartPos && event) {
      this.isTimelineDragging = false;
      this.timelineMouseDownPos = -1;
      this.changePlayheadPosition(event);
    }
    if (!this.isFitted && this.isTimelineDragging) {
      this.isTimelineDragging = false;
    }
    if (this.isPlayheadDragging) {
      this.isPlayheadDragging = false;
      this.playheadPreviousLeft = this.playheadCurrentLeft;
      const timeMs =
        this.pixelsToSecondsConvert(
          this.playheadCurrentLeft + this.virtualScrollLeft,
        ) * 1000;
      this.goToTime(timeMs);
    }
  }

  // --------------- Seek ---------------

  goToTime(timeMs: number): void {
    let clamped: number;
    if (this.zoomedSegmentIndex != null && this.useSegmentRulerBase) {
      const globalMs = this.zoomedSegmentStartMs + timeMs;
      clamped = Math.max(
        this.zoomedSegmentStartMs,
        Math.min(globalMs, this.zoomedSegmentStartMs + this.durationMs),
      );
    } else {
      clamped = Math.max(0, Math.min(timeMs, this.durationMs));
    }
    if (DEBUG_SEEK) {
      const orderIds = this.clipsByOrder.map((c) => `${c.order}:${c.id.slice(-6)}`).join(",");
      csSeekLog("goToTime", {
        mode: this.state?.mode,
        editTimeMsIn: timeMs,
        durationMs: this.durationMs,
        clampedMs: clamped,
        clipsByOrderLength: this.clipsByOrder.length,
        orderIds: orderIds || "(none)",
      });
    }
    this.seekTo.emit(clamped);
    if (!this.isFitted) {
      this.translateTimeline();
    }
  }

  // --------------- Playhead position update ---------------

  setPlayheadHeight(): void {
    if (!this.Timeline?.nativeElement?.parentElement) return;
    const ph = this.Timeline.nativeElement.parentElement.offsetHeight;
    this.playheadLineHeight = ph - 11;
    this.Playhead.nativeElement.style.display = "flex";
    this.componentHeight = ph;
  }

  updatePlayheadPosition(): void {
    if (this.isPlayheadDragging || !this.durationMs) return;
    const currentTimeMs = this.state?.currentTimeMs ?? 0;
    const displayTimeMs =
      this.zoomedSegmentIndex != null && this.useSegmentRulerBase
        ? currentTimeMs - this.zoomedSegmentStartMs
        : currentTimeMs;
    const pixels = Math.max(
      0,
      Math.min(
        (displayTimeMs / 1000) * this.pixelsPerSecond,
        (this.durationMs / 1000) * this.pixelsPerSecond,
      ),
    );
    if (pixels < 0) return;
    let timelineLeft = pixels - this.playheadHalfWidth - this.virtualScrollLeft;
    this.playheadCurrentLeft = this.playheadPreviousLeft = pixels;
    if (DEBUG_SEEK && this.state?.mode === "EDIT" && this.clipsByOrder.length > 0) {
      const lastLog = (this as { _lastPlayheadLog?: number })._lastPlayheadLog;
      const now = Date.now();
      if (lastLog == null || now - lastLog > 500) {
        (this as { _lastPlayheadLog?: number })._lastPlayheadLog = now;
        csSeekLog("updatePlayheadPosition", {
          stateCurrentTimeMs: currentTimeMs,
          displayMs: currentTimeMs,
          pixels: Math.round(pixels),
          timelineLeft: Math.round(timelineLeft),
          clipsByOrderLength: this.clipsByOrder.length,
        });
      }
    }

    // When playing and zoomed: scroll to next batch when playhead reaches the end of the viewport
    if (
      !this.isFitted &&
      this.state?.isPlaying &&
      this.Timeline?.nativeElement &&
      timelineLeft >=
        this.Timeline.nativeElement.clientWidth - 2 * this.playheadHalfWidth
    ) {
      this.translateTimeline("next");
      timelineLeft = pixels - this.playheadHalfWidth - this.virtualScrollLeft;
    }

    if (this.Playhead?.nativeElement) {
      this.Playhead.nativeElement.style.left = `${timelineLeft}px`;
    }
    this.playheadPositionInMs = this.state?.currentTimeMs ?? 0;
  }

  // --------------- Translate (from content-control) ---------------

  translateTracks(x: number): void {
    if (this.Tracks) {
      this.Tracks.forEach((t) => {
        t.nativeElement.style.transform = `translateX(-${x}px)`;
      });
    }
    if (this.SegmentsContainer?.nativeElement) {
      this.SegmentsContainer.nativeElement.style.transform = `translateX(-${x}px)`;
    }
  }

  /** Max scroll so viewport right edge does not go past end of media. */
  private getMaxScrollLeft(): number {
    if (this.isFitted) return 0;
    const vs = this.getScrollContainer();
    if (!vs) return 0;
    const viewportWidth = vs.clientWidth;
    return Math.max(0, this.timelineWidth - viewportWidth);
  }

  translateTimeline(
    type: "afterZoom" | "next" | "previous" = "afterZoom",
  ): void {
    if (!this.durationMs) return;
    const vs = this.getScrollContainer();
    if (!vs) return;
    const viewportWidth = vs.clientWidth;
    const maxScroll = this.getMaxScrollLeft();
    let nv = 0;

    if (!this.isFitted) {
      const currentTimeMs = this.state?.currentTimeMs ?? 0;
      const displayMs =
        this.zoomedSegmentIndex != null && this.useSegmentRulerBase
          ? Math.max(
              0,
              Math.min(
                currentTimeMs - this.zoomedSegmentStartMs,
                this.durationMs,
              ),
            )
          : currentTimeMs;
      const currentTimeSec = displayMs / 1000;
      const playheadPx = currentTimeSec * this.pixelsPerSecond;
      nv = playheadPx;
      switch (type) {
        case "afterZoom": {
          // Center playhead in view when possible; otherwise keep at start or end
          if (playheadPx > viewportWidth / 2) {
            nv = playheadPx - viewportWidth / 2;
            const pixelsLeft =
              (this.durationSec - currentTimeSec) * this.pixelsPerSecond;
            if (pixelsLeft < viewportWidth / 2) {
              nv -= viewportWidth / 2 - pixelsLeft;
            }
          } else {
            nv = 0;
          }
          break;
        }
        case "next":
          // Scroll so playhead is at left edge (move to next "batch")
          nv = playheadPx;
          break;
        case "previous":
          nv = playheadPx - viewportWidth;
          break;
        default:
          break;
      }
      nv = Math.max(0, Math.min(nv, maxScroll));
    }
    this.setScrollLeft(nv);
    this.translateTracks(nv);
  }

  // --------------- Click handlers ---------------

  onClipClick(clipId: string, event: MouseEvent): void {
    event.stopPropagation();
    this.clipSelected.emit(clipId);
  }

  onMarkerClick(markerId: string): void {
    this.markerSelected.emit(markerId);
  }

  onMarkerTextChange(marker: ClippingStudioMarker, text: string): void {
    this.markerTextChange.emit({ markerId: marker.id, text });
  }

  onTextSegmentClick(te: ClippingStudioTextEvent, event: MouseEvent): void {
    event.stopPropagation();
    this.textSelected.emit(te.id);
  }

  onGraphicSegmentClick(g: ClippingStudioGraphicEvent, event: MouseEvent): void {
    event.stopPropagation();
    this.graphicSelected.emit(g.id);
  }

  onSubtitleSegmentClick(sub: ClippingStudioSubtitleEvent, event: MouseEvent): void {
    event.stopPropagation();
    const seekMs =
      this.state?.mode === "EDIT" && this.clips.length > 0
        ? this.sourceToEditTimeMs(sub.StartTimeMS)
        : sub.StartTimeMS;
    this.seekTo.emit(seekMs);
    this.subtitleSelected.emit(sub.id);
  }

  onAudioMuteClick(): void {
    this.audioMutedChange.emit(!this.audioMuted);
  }

  audioTrackMenuOpen = false;

  get mainAudioTracks(): ClippingStudioMainAudioTrack[] {
    return this.state?.mainAudioTracks ?? [];
  }

  get selectedMainAudioMediaId(): number | null {
    const resolved = resolveMainAudioSelection(
      this.state?.audio,
      this.mainAudioTracks,
    );
    return resolved.selection.kind === "track"
      ? resolved.selection.mediaId
      : null;
  }

  get mainAudioSelectionFullLabel(): string {
    const id = this.selectedMainAudioMediaId;
    if (id == null) return MAIN_AUDIO_EMBEDDED_LABEL;
    const track = this.mainAudioTracks.find((t) => t.id === id);
    return track ? mainAudioTrackFullLabel(track) : MAIN_AUDIO_EMBEDDED_LABEL;
  }

  get mainAudioSelectionTruncatedLabel(): string {
    return truncateMainAudioLabel(this.mainAudioSelectionFullLabel);
  }

  trackFullLabel(track: ClippingStudioMainAudioTrack): string {
    return mainAudioTrackFullLabel(track);
  }

  trackTruncatedLabel(track: ClippingStudioMainAudioTrack): string {
    return truncateMainAudioLabel(mainAudioTrackFullLabel(track));
  }

  toggleAudioTrackMenu(event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();
    if (this.mainAudioTracks.length === 0) return;
    this.audioTrackMenuOpen = !this.audioTrackMenuOpen;
  }

  onSelectMainAudioTrack(mediaId: number | null, event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.audioTrackMenuOpen = false;
    if (mediaId === this.selectedMainAudioMediaId) return;
    this.mainAudioSelectionChange.emit(mediaId);
  }

  @HostListener("document:click")
  onDocumentClickCloseAudioMenu(): void {
    if (this.audioTrackMenuOpen) {
      this.audioTrackMenuOpen = false;
    }
  }

  onClipsDrop(event: CdkDragDrop<ClippingStudioClip[]>): void {
    if (event.previousIndex === event.currentIndex) return;
    const ordered = this.clipsByOrder.slice();
    const moved = ordered.splice(event.previousIndex, 1)[0];
    ordered.splice(event.currentIndex, 0, moved);
    this.clipReordered.emit(ordered.map((c) => c.id));
  }

  onMamDrop(event: CdkDragDrop<unknown[]>): void {
    if (this.timelineDisabled) return;
    if (!this.manager?.state?.media) return;
    const item = event.item?.data as ClippingStudioMamItem | undefined;
    if (!item || item.mamMediaId == null || (item.durationMs ?? 0) <= 0) return;
    this.mamAddRequest.emit(item);
  }

  emptyEventAction(event: Event): void {
    event.preventDefault();
    event.stopImmediatePropagation();
  }

  // --------------- Recalculate ---------------

  /** Called by parent via ViewChild when zoom changes (mirrors content-control onChangeEffectingTimeline). */
  onChangeEffectingTimeline(recalculateBlockWidth = false): void {
    if (this.blockDuration != null) {
      this.timelineBlockDuration = this.blockDuration;
    }
    if (recalculateBlockWidth) {
      this.calculateTimelineBlockWidth();
    }
    this.calculateVideoTimeline();
    setTimeout(() => {
      this.translateTimeline();
      if (this.TimelineVirtualScroll) {
        this.TimelineVirtualScroll.checkViewportSize();
      }
      this.resizeTracks();
      this.updatePlayheadPosition();
      if (this.Playhead?.nativeElement) {
        this.setPlayheadHeight();
      }
    }, 0);
  }

  private recalculate(): void {
    if (!this.durationMs) {
      this.timeBlocks = [];
      return;
    }

    if (this.isFitted) {
      this.autoCalculateBlockDuration();
      this.calculateTimelineBlockWidth();
    } else if (this.blockDuration != null) {
      this.timelineBlockDuration = this.blockDuration;
      // When zoomed (segment or full), keep timelineBlockWidth from last fit — do not recalc.
    }
    this.calculateVideoTimeline();

    setTimeout(() => {
      this.translateTimeline();
      this.updatePlayheadPosition();
      if (this.Playhead?.nativeElement) {
        this.setPlayheadHeight();
      }
      if (this.TimelineVirtualScroll) {
        this.TimelineVirtualScroll.checkViewportSize();
      }
      this.resizeTracks();
    }, 0);
  }

  private autoCalculateBlockDuration(): void {
    const containerWidth = this.Timeline?.nativeElement?.offsetWidth ?? 800;
    const targetBlockCount = Math.max(4, Math.floor(containerWidth / 100));
    const rawDuration = this.durationSec / targetBlockCount;
    this.timelineBlockDuration = this.pickBlockDurationStep(rawDuration);
  }

  /** Pick a step from the grid for the given raw duration (seconds). Used for both global and per-segment block duration. */
  private pickBlockDurationStep(rawDurationSec: number): number {
    const steps = [
      1, 2, 3, 4, 5, 7, 8, 10, 12, 13, 15, 20, 25, 30, 40, 50, 60, 80, 100,
      120, 180, 240, 300, 400, 500, 600, 1000, 1400, 1800, 2400, 3000, 3600,
    ];
    for (const step of steps) {
      if (step >= rawDurationSec) return step;
    }
    return steps[steps.length - 1];
  }

  private calculateTimelineBlockWidth(): void {
    if (this.isFitted) {
      this.timelineBlockWidth = this.timelineBlockDuration * this.pixelsPerSecond;
    }
    // When zoomed, do not overwrite timelineBlockWidth — it stays from last fit (segment or full).
  }

  /**
   * Returns the ruler label for a given global time (seconds).
   * In SOURCE mode with appended MAM segments, returns segment-relative time (0 to segment duration); otherwise returns global time.
   */
  private getRulerLabelForGlobalTimeSec(globalTimeSec: number): string {
    if (this.state?.mode !== "SOURCE") {
      return this.formatTime(globalTimeSec * 1000);
    }
    const appended = this.state?.appendedMamSegments ?? [];
    if (appended.length === 0) {
      return this.formatTime(globalTimeSec * 1000);
    }
    const mainSec = (this.state?.media?.durationMs ?? 0) / 1000;
    const segmentStartSec: number[] = [0, mainSec];
    let cursorSec = mainSec;
    for (const seg of appended) {
      cursorSec += seg.durationMs / 1000;
      segmentStartSec.push(cursorSec);
    }
    let segmentIndex = segmentStartSec.length - 1;
    for (let i = 0; i < segmentStartSec.length - 1; i++) {
      if (globalTimeSec < segmentStartSec[i + 1]) {
        segmentIndex = i;
        break;
      }
    }
    const segmentStart = segmentStartSec[segmentIndex];
    const localSec = Math.max(0, globalTimeSec - segmentStart);
    return this.formatTime(localSec * 1000);
  }

  private calculateVideoTimeline(): void {
    if (!this.durationSec) return;

    if (this.timelineBlockWidth === 0 || this.isFitted) {
      this.calculateTimelineBlockWidth();
    }

    const tickWidth = 1;
    this.timeBlocks = [];
    this._timelineBlocksLength = 0;

    if (this.useSegmentRuler) {
      const mainSec = (this.state?.media?.durationMs ?? 0) / 1000;
      const segmentDurationsSec: number[] = [mainSec];
      for (const seg of this.state?.appendedMamSegments ?? []) {
        segmentDurationsSec.push(seg.durationMs / 1000);
      }

      let segmentBlockDuration: number;
      let segmentBlockWidth: number;

      if (this.isFitted) {
        const containerWidth = this.Timeline?.nativeElement?.offsetWidth ?? 800;
        const segmentCount = segmentDurationsSec.length;
        const targetBlockCountPerSegment = Math.max(
          2,
          Math.floor(containerWidth / 100) / segmentCount,
        );
        for (let segmentIndex = 0; segmentIndex < segmentDurationsSec.length; segmentIndex++) {
          const segmentDurationSec = segmentDurationsSec[segmentIndex];
          segmentBlockDuration = this.pickBlockDurationStep(
            segmentDurationSec / targetBlockCountPerSegment,
          );
          segmentBlockWidth =
            segmentBlockDuration * this.pixelsPerSecond;

          let remainingDuration = segmentDurationSec;
          let currentTime = 0;

          while (remainingDuration > 0) {
            const ticks: TimeBlock["ticks"] = [];
            const isLastBlock = remainingDuration < segmentBlockDuration;

            const blockWidth = isLastBlock
              ? segmentBlockWidth *
                (remainingDuration / segmentBlockDuration)
              : segmentBlockWidth;

            ticks.push({ x: 0, y1: 35, y2: 45, width: tickWidth });

            const actualBlockDuration = isLastBlock
              ? remainingDuration
              : segmentBlockDuration;
            const tickStepPx = segmentBlockWidth / 10;
            for (let j = 1; j < 10; j++) {
              const smallTickX = j * tickStepPx;
              if (smallTickX >= blockWidth - 0.5) break;
              if (
                (j * actualBlockDuration) / 10 >=
                remainingDuration
              )
                break;
              ticks.push({ x: smallTickX, y1: 40, y2: 45, width: tickWidth });
            }

            const isFirstBlockOfSegment = currentTime === 0;
            const showZeroLabel = isFirstBlockOfSegment && segmentIndex === 0;
            const timestamp = showZeroLabel
              ? this.formatTime(0)
              : isFirstBlockOfSegment
                ? ""
                : this.formatTime(currentTime * 1000);

            this.timeBlocks.push({
              ticks,
              width: blockWidth,
              timestamp,
            });

            remainingDuration -= segmentBlockDuration;
            currentTime += segmentBlockDuration;
          }
        }
      } else {
        segmentBlockDuration = this.timelineBlockDuration;
        segmentBlockWidth = this.timelineBlockWidth;

        for (let segmentIndex = 0; segmentIndex < segmentDurationsSec.length; segmentIndex++) {
          const segmentDurationSec = segmentDurationsSec[segmentIndex];
          let remainingDuration = segmentDurationSec;
          let currentTime = 0;

          while (remainingDuration > 0) {
            const ticks: TimeBlock["ticks"] = [];
            const isLastBlock = remainingDuration < segmentBlockDuration;

            const blockWidth = isLastBlock
              ? segmentBlockWidth *
                (remainingDuration / segmentBlockDuration)
              : segmentBlockWidth;

            ticks.push({ x: 0, y1: 35, y2: 45, width: tickWidth });

            const actualBlockDuration = isLastBlock
              ? remainingDuration
              : segmentBlockDuration;
            const tickStepPx = segmentBlockWidth / 10;
            for (let j = 1; j < 10; j++) {
              const smallTickX = j * tickStepPx;
              if (smallTickX >= blockWidth - 0.5) break;
              if (
                (j * actualBlockDuration) / 10 >=
                remainingDuration
              )
                break;
              ticks.push({ x: smallTickX, y1: 40, y2: 45, width: tickWidth });
            }

            const isFirstBlockOfSegment = currentTime === 0;
            const showZeroLabel = isFirstBlockOfSegment && segmentIndex === 0;
            const timestamp = showZeroLabel
              ? this.formatTime(0)
              : isFirstBlockOfSegment
                ? ""
                : this.formatTime(currentTime * 1000);

            this.timeBlocks.push({
              ticks,
              width: blockWidth,
              timestamp,
            });

            remainingDuration -= segmentBlockDuration;
            currentTime += segmentBlockDuration;
          }
        }
      }
    } else {
      let remainingDuration = this.durationSec;
      let currentTime = 0;

      while (remainingDuration > 0) {
        const ticks: TimeBlock["ticks"] = [];
        const isLastBlock = remainingDuration < this.timelineBlockDuration;

        const blockWidth = isLastBlock
          ? this.timelineBlockWidth *
            (remainingDuration / this.timelineBlockDuration)
          : this.timelineBlockWidth;

        ticks.push({ x: 0, y1: 35, y2: 45, width: tickWidth });

        const actualBlockDuration = isLastBlock
          ? remainingDuration
          : this.timelineBlockDuration;
        const tickStepPx = this.timelineBlockWidth / 10;
        for (let j = 1; j < 10; j++) {
          const smallTickX = j * tickStepPx;
          if (smallTickX >= blockWidth - 0.5) break;
          if (
            (j * actualBlockDuration) / 10 >=
            remainingDuration
          )
            break;
          ticks.push({ x: smallTickX, y1: 40, y2: 45, width: tickWidth });
        }

        this.timeBlocks.push({
          ticks,
          width: blockWidth,
          timestamp:
            this.zoomedSegmentIndex != null && this.useSegmentRulerBase
              ? this.formatTime(currentTime * 1000)
              : this.getRulerLabelForGlobalTimeSec(currentTime),
        });

        remainingDuration -= this.timelineBlockDuration;
        currentTime += this.timelineBlockDuration;
      }
    }

    this._timelineBlocksLength = this.timeBlocks
      .map((x) => x.width)
      .reduce((partial, a) => partial + a, 0);
  }

  /** Resize tracks and segments width to match total timeline blocks length (for zoomed mode). */
  private resizeTracks(): void {
    const sw = this._timelineBlocksLength + this.playheadHalfWidth;
    const widthVal = this.isFitted ? "" : `${sw}px`;
    if (this.TracksContainer?.nativeElement) {
      this.TracksContainer.nativeElement.style.width = widthVal;
    }
    if (this.SegmentsContainer?.nativeElement) {
      this.SegmentsContainer.nativeElement.style.width = widthVal;
    }
  }
}
